import type {
  ReceptionScoreOperation,
  ReceptionScoreState,
} from 'services/api/receptionScoreCommand';

interface InvitationDependencies {
  getState: () => Promise<ReceptionScoreState>;
  send: (id: string, revision: number) => Promise<ReceptionScoreOperation>;
  query: (id: string) => Promise<ReceptionScoreOperation>;
  createId: () => string;
  loadPending: () => string | null;
  savePending: (id: string) => void;
  clearPending: () => void;
  isActive: () => boolean;
}

export function invitationErrorCode(error: unknown): string {
  const value = error as {
    response?: { data?: { errorCode?: string; msg?: string } };
    message?: string;
  };
  return (
    value?.response?.data?.errorCode ||
    value?.response?.data?.msg ||
    value?.message ||
    'SEND_FAILED'
  );
}

const terminal = (result: ReceptionScoreOperation) =>
  result.operationStatus === 'SUCCEEDED' || result.operationStatus === 'FAILED';

/** One click has a hard five-second deadline, including state lookup and slow HTTP requests. */
export async function runScoreInvitation(
  deps: InvitationDependencies,
): Promise<ReceptionScoreOperation> {
  let active = true;
  let deadline: ReturnType<typeof setTimeout>;
  const check = () => {
    if (!active || !deps.isActive()) throw new Error('SERVER_BUSY');
  };
  const storage = <T>(fn: () => T): T => {
    try {
      return fn();
    } catch {
      throw new Error('STORAGE_FAILED');
    }
  };
  const never = () => new Promise<ReceptionScoreOperation>(() => {});
  const task = async () => {
    let requestId = storage(deps.loadPending);
    let posted = never();
    if (!requestId) {
      const state = await deps.getState();
      check();
      if (state.autoStatus === 'UNKNOWN') throw new Error('PREVIOUS_SEND_UNKNOWN');
      requestId = deps.createId();
      storage(() => deps.savePending(requestId!));
      posted = deps
        .send(requestId, state.stateRevision)
        .then((result) => {
          check();
          return terminal(result) ? result : never();
        })
        .catch((error) => {
          check();
          const status = error?.response?.status;
          if (status >= 400 && status < 500) {
            storage(deps.clearPending);
            throw error;
          }
          // Network/server failures do not prove the command was rejected. Query its original ID.
          return never();
        });
    }
    const poll = async (): Promise<ReceptionScoreOperation> => {
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        check();
        let result: ReceptionScoreOperation;
        try {
          result = await deps.query(requestId!);
        } catch {
          check();
          continue;
        }
        check();
        if (terminal(result)) return result;
      }
    };
    const result = await Promise.race([posted, poll()]);
    check();
    storage(deps.clearPending);
    return result;
  };
  try {
    return await Promise.race([
      task(),
      new Promise<ReceptionScoreOperation>((_, reject) => {
        deadline = setTimeout(() => reject(new Error('SERVER_BUSY')), 5000);
      }),
    ]);
  } finally {
    active = false;
    clearTimeout(deadline!);
  }
}
